import { api } from "./api";
import type { MarketingRun } from "./marketing";

/**
 * The automation: the clock over the sender, and the hard stop above both.
 *
 * Nothing here decides whether a letter may go out. That is the sender's, and
 * the same rules answer a batch an operator starts by hand - see the API's
 * `lib/send/senderCap.ts`. What this page controls is WHEN the question is
 * asked, and the one switch that stops it being asked at all.
 */

/** Why the automation would pass a campaign over. Mirrors the server's `AutoSkip`. */
export type AutoSkip = "ok" | "not-active" | "auto-off" | "before-start" | "after-end";

export interface AutomationSettings {
  enabled: boolean;
  intervalMinutes: number;
  campaignsPerTick: number;
  globalDailyCap: number;
  timezone: string;
  warmupEnabled: boolean;
}

export interface MarketingStop {
  stopped: boolean;
  reason: string;
  at: string;
  by: string | null;
}

export interface RampView {
  startedAt: string;
  /** 0 on the first day of this sender's life. */
  dayIndex: number;
  /** Today's ceiling, or null once the ramp is over. */
  ceiling: number | null;
  /** How many days the ramp lasts in total. */
  days: number;
  /** The first day's ceiling. */
  start: number;
}

export interface TodayView {
  sentToday: number;
  /** Letters left today, sender-wide. */
  left: number;
  ceiling: number;
  /** Which limit is doing the limiting. */
  binding: "warmup" | "global" | "none";
}

export interface AutomationCampaign {
  id: string;
  name: string;
  status: string;
  autoEnabled: boolean;
  startsAt: string | null;
  endsAt: string | null;
  skip: AutoSkip;
  lastRunAt: string | null;
}

export interface AutomationView {
  settings: AutomationSettings;
  stop: MarketingStop;
  /** Who is sending, as the ramp measures it. Never a credential. */
  sender: { fingerprint: string | null; configured: boolean };
  /** Null when the ramp is switched off, or when there is no sender to measure. */
  ramp: RampView | null;
  today: TodayView;
  lastScheduledAt: string | null;
  nextDueAt: string | null;
  campaigns: AutomationCampaign[];
  recentRuns: MarketingRun[];
}

export const getAutomation = (): Promise<AutomationView> =>
  api.get("/admin/crm/marketing/automation");

export const saveAutomation = (body: Partial<AutomationSettings>): Promise<{ settings: AutomationSettings }> =>
  api.put("/admin/crm/marketing/automation", body);

/** One tick, now. Ignores the switch and the interval, and nothing else. */
export const runAutomationNow = (): Promise<{ accepted: boolean }> =>
  api.post("/admin/crm/marketing/automation/run");

/** The reason is required, and it is stored. Somebody asks about it later. */
export const pullMarketingStop = (reason: string): Promise<{ stop: MarketingStop }> =>
  api.post("/admin/crm/marketing/stop", { reason });

export const releaseMarketingStop = (): Promise<{ stop: MarketingStop }> =>
  api.del("/admin/crm/marketing/stop");
